import { Mutex } from "async-mutex";
import { create } from "zustand";
import {
  createJSONStorage,
  persist,
  subscribeWithSelector,
} from "zustand/middleware";
import * as SharedChatCommandSchema from "../../darvin-shared/schema/zod/agent/chat-command.schema";
import * as SharedChatResponse from "../../darvin-shared/schema/zod/agent/chat-response.schema";
import * as SharedMessageTypes from "../../darvin-shared/schema/zod/agent/message-types.schema";
import { CHAT_HISTORY_PAGE_SIZE, ChatApi, ThreadStatus } from "../api/chatApi";
import { HttpNotEnoughTokensException } from "../model/httpException";
import {
  SseConnectionManager,
  SseMessageHandler,
} from "../services/sseConnectionManager";
import { useModelStore } from "./useModelStore";

export enum ProjectError {
  NOT_ENOUGH_TOKENS = "notEnoughTokens",
  TIMEOUT = "timeout",
}

export enum HistoryLoadingStatus {
  IDLE = "idle",
  LOADING = "loading", // Initial load or reload
  LOADING_MORE = "loadingMore", // Paginated load (older messages)
}

const mutex = new Mutex();

interface ProjectActions {
  initialize: (
    projectId: string,
    initialPrompt: string | null,
    initialImage: File | null,
  ) => Promise<void>;
  sendMessage: (message: string, image: File | null) => Promise<void>;
  fixCompilationErrorForMessage: (messageId: string) => Promise<void>;
  rollback: (
    checkpointId?: string,
    originalCheckpointId?: string,
  ) => Promise<void>;
  loadMoreHistory: () => Promise<void>;
  loadLastUserMessage: () => Promise<{
    lastUserMessage: SharedChatResponse.MessageBody | null;
    hasMore: boolean;
  }>;
  dispose: () => void;
  disposeSSE: () => void;
  refresh: () => Promise<void>;
}

interface ProjectState {
  isNewProject: boolean | null;
  projectId: string | null;
  history: SharedChatResponse.MessageBody[] | null;
  initialProjectStatus: ThreadStatus | null;
  latestMsg: SharedChatResponse.MessageBody | null;
  error: ProjectError | null;
  hasMoreHistory: boolean;
  historyLoadingStatus: HistoryLoadingStatus;

  actions: ProjectActions;
}

export const useProjectStore = create<ProjectState>()(
  persist(
    subscribeWithSelector((set, get) => {
      let connectionManager: SseConnectionManager | undefined = undefined;
      let inactivityTimer: NodeJS.Timeout | undefined = undefined;
      let oneTimeRefreshOnVisibleHandler: ((event: Event) => void) | undefined =
        undefined;
      let visibilityHandlerForInactivityCheck:
        | ((event: Event) => void)
        | undefined = undefined;
      let inactivityStartTime: number | undefined = undefined;
      const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
      let shouldReloadHistoryOnChatMessage: boolean = false;
      let idOfMessageToReloadChatHistoryAfter: string | null = null;
      let idOfMessageBeforeReload: string | null = null;

      // Pending reload mechanism: if reload is requested while another operation is in progress,
      // queue it to execute after the current operation completes
      let pendingReloadParams: {
        messageIdToOmit?: string;
        isFirstLoad?: boolean;
      } | null = null;

      function registerVisibilityHandlerForInactivityCheck() {
        if (!visibilityHandlerForInactivityCheck) {
          visibilityHandlerForInactivityCheck = async () => {
            if (document.visibilityState === "visible") {
              if (isInactivityTimeout()) {
                await onInactivityTimeout();
              }
            }
          };
          document.addEventListener(
            "visibilitychange",
            visibilityHandlerForInactivityCheck,
          );
        }
      }

      function unregisterVisibilityHandlerForInactivityCheck() {
        if (visibilityHandlerForInactivityCheck) {
          document.removeEventListener(
            "visibilitychange",
            visibilityHandlerForInactivityCheck,
          );
          visibilityHandlerForInactivityCheck = undefined;
        }
      }

      function registerOneTimeRefreshOnVisibleHandler() {
        if (oneTimeRefreshOnVisibleHandler) {
          document.removeEventListener(
            "visibilitychange",
            oneTimeRefreshOnVisibleHandler,
          );
        }
        oneTimeRefreshOnVisibleHandler = async () => {
          if (document.visibilityState === "visible") {
            await get().actions.refresh();
            document.removeEventListener(
              "visibilitychange",
              oneTimeRefreshOnVisibleHandler!,
            );
            oneTimeRefreshOnVisibleHandler = undefined;
          }
        };
        document.addEventListener(
          "visibilitychange",
          oneTimeRefreshOnVisibleHandler,
        );
      }

      function unregisterOneTimeRefreshOnVisibleHandler() {
        if (oneTimeRefreshOnVisibleHandler) {
          document.removeEventListener(
            "visibilitychange",
            oneTimeRefreshOnVisibleHandler,
          );
          oneTimeRefreshOnVisibleHandler = undefined;
        }
      }

      function clearError(errorToClear: ProjectError) {
        if (get().error !== errorToClear) {
          return;
        }
        set({ error: null });
      }

      function setError(errorToSet: ProjectError) {
        set({ error: errorToSet });
      }

      function stopInactivityTimer() {
        if (inactivityTimer) {
          clearTimeout(inactivityTimer);
        }
        inactivityTimer = undefined;
        inactivityStartTime = undefined;
      }

      function startInactivityTimer(startTime?: number) {
        stopInactivityTimer();

        // Record when the timer started
        const now = Date.now();
        inactivityStartTime = startTime ? startTime : now;
        const elapsedSinceStart = now - inactivityStartTime;
        if (elapsedSinceStart >= INACTIVITY_TIMEOUT_MS) {
          // Timeout is already due in moment of staring the timer
          onInactivityTimeout();
          return;
        }

        // Set up the timeout as a fallback for when tab is active
        inactivityTimer = setTimeout(
          onInactivityTimeout,
          INACTIVITY_TIMEOUT_MS - elapsedSinceStart,
        );
      }

      async function onInactivityTimeout() {
        stopInactivityTimer();
        setError(ProjectError.TIMEOUT);
        await loadChatHistory();
      }

      function isInactivityTimeout() {
        if (!inactivityStartTime) {
          return false;
        }
        const elapsedTime = Date.now() - inactivityStartTime;
        return elapsedTime >= INACTIVITY_TIMEOUT_MS;
      }

      function isTerminalMessage(msg: SharedChatResponse.MessageBody) {
        return (
          msg.type === SharedMessageTypes.MessageTypeEnum.enum.jobDone ||
          msg.type === SharedMessageTypes.MessageTypeEnum.enum.error
        );
      }

      function isMessageTriggeringHistoryReload(
        msg: SharedChatResponse.MessageBody,
      ) {
        // Job done for rollbacks should reload history no matter what
        return (
          msg.type === SharedMessageTypes.MessageTypeEnum.enum.jobDone &&
          msg.content.includes("command-rollback")
        );
      }

      function disposeConnectionManager() {
        connectionManager?.dispose();
        connectionManager = undefined;
      }

      function createConnectionManager(messageHandler: SseMessageHandler) {
        const projectId = get().projectId;
        if (!projectId) {
          console.warn(
            "Failed to create connection manager: Project ID is not set",
          );
          return undefined;
        }
        disposeConnectionManager();
        connectionManager = new SseConnectionManager(projectId, messageHandler);
        return connectionManager;
      }

      async function startListening() {
        await createConnectionManager({
          onMessage: async (msg) => {
            try {
              const data = JSON.parse(msg.data);
              const body = SharedChatResponse.MessageBodySchema.parse(
                data.body,
              );
              console.log(
                `Event received in SSE: ${data.body.type}`,
                data.body,
              );
              await handleSseMessage(body);
            } catch (error) {
              console.error("Error parsing SSE message:", error);
            }
          },
          onAbort: (wasManualAbort: boolean) => {
            if (wasManualAbort) {
              return;
            }
            registerOneTimeRefreshOnVisibleHandler();
          },
        })?.start();
      }

      // Process any pending reload that was queued while another operation was in progress
      async function processPendingReload() {
        if (pendingReloadParams) {
          const params = pendingReloadParams;
          pendingReloadParams = null; // Clear before executing to avoid infinite loop
          await loadChatHistory(params);
        }
      }

      async function loadChatHistory(params?: {
        messageIdToOmit?: string;
        isFirstLoad?: boolean;
      }) {
        const currentStatus = get().historyLoadingStatus;

        // If another operation is in progress, queue this reload to execute after
        if (currentStatus !== HistoryLoadingStatus.IDLE) {
          // Start fetching in parallel (optimistic), but don't update state yet
          pendingReloadParams = params || {};
          return;
        }

        const { messageIdToOmit, isFirstLoad = false } = params || {};
        set({ historyLoadingStatus: HistoryLoadingStatus.LOADING });
        try {
          const [historyResponse, threadStatusResponse] = await Promise.all([
            ChatApi.loadChatHistory(get().projectId!, {
              limit: CHAT_HISTORY_PAGE_SIZE,
            }),
            ChatApi.getThreadStatus(get().projectId!),
          ]);
          processHistory({
            history: historyResponse.items,
            hasMore: historyResponse.hasMore,
            threadStatus: threadStatusResponse,
            messageIdToOmit,
            isFirstLoad,
          });
        } finally {
          set({ historyLoadingStatus: HistoryLoadingStatus.IDLE });
          // After completing, process any reload that was queued
          await processPendingReload();
        }
      }

      async function loadAndMergeNewerHistory(params: {
        afterMessageId: string;
        messageIdToOmit?: string;
      }) {
        const { afterMessageId, messageIdToOmit } = params;
        const currentHistory = get().history ?? [];

        const response = await ChatApi.loadChatHistory(get().projectId!, {
          afterMessageId,
        });

        let newMessages = response.items;
        if (messageIdToOmit) {
          newMessages = newMessages.filter(
            (msg) => msg.messageId !== messageIdToOmit,
          );
        }

        // Merge: keep existing history up to afterMessageId, then append new messages
        const existingUpToAfter = currentHistory.filter(
          (msg) =>
            msg.timestamp <=
            (currentHistory.find((m) => m.messageId === afterMessageId)
              ?.timestamp ?? 0),
        );
        const mergedHistory = [...existingUpToAfter, ...newMessages];

        const latestMsg = mergedHistory.at(-1) ?? null;
        checkLastHistoryMessageTimeout(latestMsg);

        set({ history: mergedHistory, latestMsg });
      }

      async function loadMoreChatHistory() {
        const currentHistory = get().history;
        if (
          !currentHistory ||
          currentHistory.length === 0 ||
          !get().hasMoreHistory ||
          get().historyLoadingStatus !== HistoryLoadingStatus.IDLE // Prevent loading while any operation is in progress
        ) {
          return;
        }

        // Get the oldest message ID (first in the array since messages are sorted by timestamp)
        const oldestMessageId = currentHistory[0].messageId;

        set({ historyLoadingStatus: HistoryLoadingStatus.LOADING_MORE });
        try {
          const response = await ChatApi.loadChatHistory(get().projectId!, {
            limit: CHAT_HISTORY_PAGE_SIZE,
            beforeMessageId: oldestMessageId,
          });

          // Prepend older messages to existing history
          const newHistory = [...response.items, ...currentHistory];
          set({
            history: newHistory,
            hasMoreHistory: response.hasMore,
          });
        } finally {
          set({ historyLoadingStatus: HistoryLoadingStatus.IDLE });
          // After completing, process any reload that was queued while we were loading more
          await processPendingReload();
        }
      }

      function processHistory({
        history,
        hasMore,
        threadStatus,
        messageIdToOmit,
        isFirstLoad = false,
      }: {
        history: SharedChatResponse.MessageBody[];
        hasMore: boolean;
        threadStatus: ThreadStatus;
        messageIdToOmit?: string;
        isFirstLoad: boolean;
      }) {
        console.log("Loaded history", history);

        if (messageIdToOmit) {
          history = history.filter((msg) => msg.messageId !== messageIdToOmit);
        }

        const latestMsg = history.at(-1) ?? null;
        if (threadStatus.job.isInProgress) {
          checkLastHistoryMessageTimeout(latestMsg);
        } else {
          resetMessageTimeout();
        }

        // First load in the middle of the conversation stream can result in
        // incomplete chat state, so reload full history on next message
        shouldReloadHistoryOnChatMessage =
          isFirstLoad && threadStatus.job.isInProgress;

        set({
          history,
          latestMsg,
          hasMoreHistory: hasMore,
          initialProjectStatus: threadStatus,
        });
      }

      function resetMessageTimeout() {
        stopInactivityTimer();
        clearError(ProjectError.TIMEOUT);
      }

      function checkLastHistoryMessageTimeout(
        lastMessage: SharedChatResponse.MessageBody | null,
      ) {
        if (!lastMessage) {
          return;
        }

        const elapsedTimeSinceLastMsg = Date.now() - lastMessage.timestamp;
        if (elapsedTimeSinceLastMsg > INACTIVITY_TIMEOUT_MS) {
          // Last message is outdated - set error timeout and
          // stop checking inactivity timeout (timeout happened here)
          stopInactivityTimer();
          setError(ProjectError.TIMEOUT);
        } else {
          // Valid non terminal message - no error, but check for
          // inactivity since that last message
          startInactivityTimer(lastMessage.timestamp);
          clearError(ProjectError.TIMEOUT);
        }
      }

      function onProjectActivity({
        finalActivity,
      }: {
        finalActivity: boolean;
      }) {
        clearError(ProjectError.TIMEOUT);
        if (finalActivity) {
          stopInactivityTimer();
          unregisterVisibilityHandlerForInactivityCheck();
        } else {
          startInactivityTimer();
          registerVisibilityHandlerForInactivityCheck();
        }
      }

      async function handleSseMessage(msg: SharedChatResponse.MessageBody) {
        await mutex.runExclusive(async () => {
          onProjectActivity({ finalActivity: isTerminalMessage(msg) });

          if (isMessageTriggeringHistoryReload(msg)) {
            await loadChatHistory();
          }

          if (
            msg.type ===
              SharedMessageTypes.MessageTypeEnum.enum.projectConfig ||
            msg.type === SharedMessageTypes.MessageTypeEnum.enum.general ||
            msg.type === SharedMessageTypes.MessageTypeEnum.enum.tools ||
            msg.type === SharedMessageTypes.MessageTypeEnum.enum.error
          ) {
            if (shouldReloadHistoryOnChatMessage) {
              if (
                (idOfMessageToReloadChatHistoryAfter &&
                  msg.messageId !== idOfMessageToReloadChatHistoryAfter) ||
                !msg.stream ||
                ((msg.chunk?.delta?.length ?? 0) > 0 &&
                  (msg.chunk!.delta[0].path === "/topic" ||
                    msg.chunk!.delta[0].path === "/files"))
              ) {
                if (idOfMessageBeforeReload) {
                  await loadAndMergeNewerHistory({
                    afterMessageId: idOfMessageBeforeReload,
                    messageIdToOmit: msg.messageId,
                  });
                } else {
                  await loadChatHistory({ messageIdToOmit: msg.messageId });
                }
                shouldReloadHistoryOnChatMessage = false;
                idOfMessageToReloadChatHistoryAfter = null;
                idOfMessageBeforeReload = null;
              } else if (!idOfMessageToReloadChatHistoryAfter) {
                // Track the last message in history before we start tracking the reload
                const currentHistory = get().history;
                idOfMessageBeforeReload =
                  currentHistory && currentHistory.length > 0
                    ? currentHistory[currentHistory.length - 1].messageId
                    : null;
                idOfMessageToReloadChatHistoryAfter = msg.messageId;
              }
            }
          }
          set({ latestMsg: msg });
        });
      }

      return {
        isNewProject: null,
        projectId: null,
        history: null,
        initialProjectStatus: null,
        latestMsg: null,
        error: null,
        hasMoreHistory: false,
        historyLoadingStatus: HistoryLoadingStatus.IDLE,

        actions: {
          initialize: async (
            projectId: string,
            initialPrompt: string | null,
            initialImage: File | null,
          ) => {
            if (get().projectId) {
              await loadChatHistory({ isFirstLoad: true });
              startListening();
              set({ isNewProject: false });
              return;
            }

            set({ isNewProject: initialPrompt ? true : false, projectId });

            await loadChatHistory({ isFirstLoad: true });
            startListening();

            if (initialPrompt) {
              try {
                await get().actions.sendMessage(initialPrompt, initialImage);
              } catch (error) {
                // HttpNotEnoughTokensException handled by setError() subscription
                // which triggers the error message display
                if (!(error instanceof HttpNotEnoughTokensException)) {
                  throw error;
                }
              }
            }
          },

          sendMessage: async (message: string, image: File | null) => {
            try {
              onProjectActivity({ finalActivity: false });
              const model = useModelStore.getState().selectedModel;
              await ChatApi.sendMessage(
                get().projectId!,
                message,
                image,
                model,
              );

              // If call succeeded it means we have enough tokens
              clearError(ProjectError.NOT_ENOUGH_TOKENS);
            } catch (error) {
              if (error instanceof HttpNotEnoughTokensException) {
                setError(ProjectError.NOT_ENOUGH_TOKENS);
                throw error; // Re-throw so caller knows message was not sent
              } else {
                throw error;
              }
            }
          },

          fixCompilationErrorForMessage: async (messageId: string) => {
            await ChatApi.sendCommand(get().projectId!, {
              command:
                SharedChatCommandSchema.ChatCommandTypeEnum.enum
                  .fixCompilationError,
              args: {
                errorMessageId: messageId,
              },
            });
          },

          rollback: async (
            checkpointId?: string,
            originalCheckpointId?: string,
          ) => {
            let finalCheckpointId = checkpointId;

            if (!finalCheckpointId) {
              // Load the most recent user message to get its checkpoint
              const response = await ChatApi.loadChatHistory(get().projectId!, {
                limit: 1,
                messageTypes: [
                  SharedMessageTypes.MessageTypeEnum.enum.userMessage,
                ],
              });
              if (response.items.length === 0) {
                return;
              }

              finalCheckpointId = response.items[0].checkpointId;
            }

            if (!finalCheckpointId) {
              throw new Error("No checkpoint for rollback found!");
            }

            try {
              const args: {
                checkpointId: string;
                originalCheckpointId?: string;
              } = {
                checkpointId: finalCheckpointId!,
              };

              if (originalCheckpointId) {
                args.originalCheckpointId = originalCheckpointId;
              }

              await ChatApi.sendCommand(get().projectId!, {
                command:
                  SharedChatCommandSchema.ChatCommandTypeEnum.enum.rollback,
                args,
              });
              clearError(ProjectError.NOT_ENOUGH_TOKENS);
            } catch (error) {
              if (error instanceof HttpNotEnoughTokensException) {
                setError(ProjectError.NOT_ENOUGH_TOKENS);
                throw error; // Re-throw so caller knows rollback was not executed
              } else {
                throw error;
              }
            }
          },

          loadMoreHistory: async () => {
            if (get().historyLoadingStatus !== HistoryLoadingStatus.IDLE) {
              return;
            }
            await loadMoreChatHistory();
          },

          loadLastUserMessage: async () => {
            const projectId = get().projectId;
            if (!projectId) {
              return {
                lastUserMessage: null,
                hasMore: false,
              };
            }
            const response = await ChatApi.loadChatHistory(get().projectId!, {
              limit: 2,
              messageTypes: [
                SharedMessageTypes.MessageTypeEnum.enum.userMessage,
              ],
            });
            return {
              lastUserMessage: response.items.at(-1) ?? null,
              hasMore: response.items.length > 1,
            };
          },

          dispose: () => {
            disposeConnectionManager();
            stopInactivityTimer();
            unregisterVisibilityHandlerForInactivityCheck();
            unregisterOneTimeRefreshOnVisibleHandler();

            // Clear any pending reload
            pendingReloadParams = null;

            set({
              projectId: null,
              history: null,
              latestMsg: null,
              hasMoreHistory: false,
              historyLoadingStatus: HistoryLoadingStatus.IDLE,
              initialProjectStatus: null,
              isNewProject: null,
              error: null,
            });
          },

          disposeSSE: () => {
            if (connectionManager) {
              connectionManager.dispose();
              connectionManager = undefined;
            }
          },

          refresh: async () => {
            await loadChatHistory({ isFirstLoad: true });
          },
        },
      };
    }),
    {
      name: "project-storage",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        projectId: state.projectId,
      }),
      version: 1,
    },
  ),
);

export const useIsNewProject = () =>
  useProjectStore((state) => state.isNewProject);

export const useProjectId = () => useProjectStore((state) => state.projectId);

export const useProjectMessages = () =>
  useProjectStore((state) => state.history);

export const useHasMoreHistory = () =>
  useProjectStore((state) => state.hasMoreHistory);

export const useHistoryLoadingStatus = () =>
  useProjectStore((state) => state.historyLoadingStatus);

export const useIsLoadingMoreHistory = () =>
  useProjectStore(
    (state) => state.historyLoadingStatus === HistoryLoadingStatus.LOADING_MORE,
  );

export const useProjectActions = () =>
  useProjectStore((state) => state.actions);
